import { AutorenderApiClient } from './client/api-client';
import { UploaderController } from './core/uploader-controller';
import type { ControllerOptions } from './core/uploader-controller';
import { DEFAULT_ASSET_DELIVERY_BASE_URL } from './constants/defaults';
import type { CreateUploaderOptions, UploaderInstance } from './types';
import { AutorenderUploaderElement } from './widget/uploader-element';
import { resolveSourcesConfig, resolveThemePreset } from './resolve-config';

function ensureCustomElement() {
  if (
    typeof globalThis === 'undefined' ||
    typeof globalThis.customElements === 'undefined'
  ) {
    return;
  }
  if (!globalThis.customElements.get(AutorenderUploaderElement.tagName)) {
    globalThis.customElements.define(
      AutorenderUploaderElement.tagName,
      AutorenderUploaderElement
    );
  }
}

function resolveTarget(target: string | HTMLElement): HTMLElement {
  if (typeof target === 'string') {
    const element = document.querySelector<HTMLElement>(target);
    if (!element) {
      throw new Error(`Target element "${target}" not found`);
    }
    return element;
  }
  return target;
}

function resolveContainer(container?: string | HTMLElement): HTMLElement | null {
  if (!container) return null;
  if (typeof container === 'string') {
    const element = document.querySelector<HTMLElement>(container);
    if (!element) {
      throw new Error(`Container element "${container}" not found`);
    }
    return element;
  }
  return container;
}

function normalizeOptions(
  options: Partial<CreateUploaderOptions>,
  applyDefaults = false
): Partial<ControllerOptions> {
  const {
    type,
    theme,
    sources,
    classNames,
    labels,
    fileLayout,
    showGridFileName,
    apiKey: _apiKey,
    target: _target,
    ...rest
  } = options;

  const normalized: any = { ...rest };

  if (type !== undefined) {
    normalized.type = type === 'button' ? 'button' : 'inline';
  } else if (applyDefaults) {
    normalized.type = 'inline';
  }

  if (theme !== undefined) {
    normalized.theme = resolveThemePreset(theme);
  } else if (applyDefaults) {
    normalized.theme = resolveThemePreset('light');
  }

  if (options.palette !== undefined) {
    normalized.palette = options.palette;
  } else if (applyDefaults) {
    normalized.palette = 'at-blue';
  }

  if (sources !== undefined) {
    normalized.sources = resolveSourcesConfig(sources);
  } else if ('sources' in options) {
    // Explicitly undefined - means no sources (all disabled)
    normalized.sources = [];
  } else if (applyDefaults) {
    normalized.sources = [];
  }

  if (classNames !== undefined) {
    normalized.classNames = classNames;
  }

  if (labels !== undefined) {
    normalized.labels = labels;
  }

  if (fileLayout !== undefined) {
    normalized.fileLayout = fileLayout;
  } else if (applyDefaults) {
    normalized.fileLayout = 'list';
  }

  if (showGridFileName !== undefined) {
    normalized.showGridFileName = showGridFileName;
  } else if (applyDefaults) {
    normalized.showGridFileName = true;
  }

  if (options.assetDeliveryBaseUrl !== undefined) {
    normalized.assetDeliveryBaseUrl = options.assetDeliveryBaseUrl;
  } else if (applyDefaults) {
    normalized.assetDeliveryBaseUrl = DEFAULT_ASSET_DELIVERY_BASE_URL;
  }

  return normalized;
}

export function createUploader(options: CreateUploaderOptions): UploaderInstance {
  // If container is provided, use it as the target so the entire widget renders inside it
  const container = resolveContainer(options.container);
  const defaultTarget = resolveTarget(options.target);
  const resolvedTarget = container || defaultTarget;
  
  ensureCustomElement();

  const widget = document.createElement(
    AutorenderUploaderElement.tagName
  ) as AutorenderUploaderElement;

  // Clear target and mount widget
  resolvedTarget.innerHTML = '';
  resolvedTarget.appendChild(widget);

  const client = new AutorenderApiClient({
    apiKey: options.apiKey,
    baseUrl: options.uploadBaseUrl,
  });

  const controllerOptions = normalizeOptions(options, true) as ControllerOptions;
  
  // Only set container in options if it's different from target (for portal logic)
  // If container is the same as target, we don't need portal
  if (container && container !== resolvedTarget) {
    controllerOptions.container = container;
  } else {
    // Clear container option if it's the same as target
    controllerOptions.container = undefined;
  }

  const controller = new UploaderController(
    client,
    widget,
    controllerOptions
  );

  widget.setController(controller, controllerOptions);

  return {
    openFileDialog: () => widget.openFileDialog(),
    startUpload: () => controller.uploadAll(),
    reset: () => controller.reset(),
    destroy: () => {
      controller.cancel();
      controller.reset();
      widget.remove();
    },
    updateOptions: (partialOptions: Partial<CreateUploaderOptions>) => {
      client.updateOptions({
        ...(partialOptions.apiKey !== undefined ? { apiKey: partialOptions.apiKey } : {}),
        ...('uploadBaseUrl' in partialOptions
          ? { baseUrl: partialOptions.uploadBaseUrl }
          : {}),
      });
      const normalized = normalizeOptions(partialOptions, false);
      controller.setOptions(normalized);
      widget.updateOptions(normalized);
    },
    getState: () => controller.getState(),
  };
}

export * from './types';
export {
  AutorenderAuthError,
  AutorenderNetworkError,
  AutorenderUploadError,
} from './errors';
export function registerAutorenderUploaderElement() {
  ensureCustomElement();
}
